import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

/**
 * Thin progress bar at the very top of the viewport that
 * animates during route transitions (Suspense fallback).
 *
 * Integrates automatically — just render once in AdminLayout.
 */
const NavigationProgress = () => {
  const { pathname } = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const prevPathRef = useRef(pathname);
  const timersRef = useRef([]);

  /** Cancel all pending timers */
  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  /** Schedule a timeout and track it for cleanup */
  const schedule = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);

  useEffect(() => {
    if (pathname === prevPathRef.current) return;
    prevPathRef.current = pathname;

    // Cancel any in-flight animation from a previous transition
    clearTimers();

    // Start animation
    setVisible(true);
    setProgress(15);

    // Step through progress milestones
    const steps = [
      [30, 120],
      [55, 300],
      [70, 500],
      [82, 800],
      [88, 1200],
    ];

    steps.forEach(([value, delay]) => {
      schedule(() => setProgress(value), delay);
    });

    // After the page has had time to render, snap to 100% and fade out
    schedule(() => {
      setProgress(100);
      schedule(() => {
        setVisible(false);
        setProgress(0);
      }, 350);
    }, 1500);

    return clearTimers;
  }, [pathname, clearTimers, schedule]);

  if (!visible && progress === 0) return null;

  return (
    <Box
      role="progressbar"
      aria-valuenow={progress}
      aria-label="Navigation progress"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: (theme) => theme.zIndex.drawer + 2,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${progress}%`,
          bgcolor: 'primary.main',
          transition: progress === 100
            ? 'width 200ms ease-out, opacity 300ms ease-out'
            : 'width 400ms ease-out',
          opacity: progress === 100 ? 0 : 1,
          borderRadius: '0 2px 2px 0',
          boxShadow: '0 0 8px rgba(44, 62, 80, 0.4)',
        }}
      />
    </Box>
  );
};

export default NavigationProgress;
